import React, { useState, useEffect } from 'react';
import { CycleData, generateCycleData } from '@/utils/cycle-tracking';
import { CircularCycleView } from './CircularCycleView';
import HormoneTestPlaceholder from './HormoneTestPlaceholder';
import { CycleStatCards } from './CycleStatCards';
import { subDays } from 'date-fns';
import { useUser } from '@/context/UserContext';
import { supabase } from '@/integrations/supabase/client';
import { Card, CardContent } from '@/components/ui/card';

interface PeriodTrackingDashboardProps {
  cycleData: CycleData | null;
}

export const PeriodTrackingDashboard: React.FC<PeriodTrackingDashboardProps> = ({ 
  cycleData: initialCycleData
}) => {
  const { user } = useUser();
  const [cycleData, setCycleData] = useState<CycleData | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  
  useEffect(() => {
    const fetchUserCycleData = async () => {
      if (!user?.id) {
        if (initialCycleData) {
          setCycleData(initialCycleData);
        } else {
          // Generate placeholder data if none is provided
          const lastPeriod = subDays(new Date(), 10);
          const placeholderData = generateCycleData(lastPeriod);
          setCycleData(placeholderData);
        }
        setIsLoading(false);
        return;
      }
      
      try {
        setIsLoading(true);
        
        // Get health profile data from Supabase
        const { data: healthProfile, error } = await supabase
          .from('health_profiles')
          .select('last_period_start, last_period_duration')
          .eq('user_id', user.id)
          .single();
        
        if (error && error.code !== 'PGRST116') {
          throw error;
        }
        
        if (healthProfile?.last_period_start) {
          const lastPeriodStart = new Date(healthProfile.last_period_start);
          const periodLength = parseInt(healthProfile.last_period_duration) || 5;
          
          // Generate cycle data
          const calculatedCycleData = generateCycleData(lastPeriodStart, periodLength);
          setCycleData(calculatedCycleData);
        } else if (initialCycleData) {
          setCycleData(initialCycleData);
        } else {
          // Generate placeholder data if no data found
          const lastPeriod = subDays(new Date(), 10);
          const placeholderData = generateCycleData(lastPeriod);
          setCycleData(placeholderData);
        }
      } catch (error) {
        console.error('Error fetching cycle data:', error);
        // Still use placeholder data if there's an error
        if (initialCycleData) {
          setCycleData(initialCycleData);
        } else {
          const lastPeriod = subDays(new Date(), 10);
          const placeholderData = generateCycleData(lastPeriod);
          setCycleData(placeholderData);
        }
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchUserCycleData();
  }, [user?.id, initialCycleData]);
  
  if (isLoading) return (
    <div className="flex justify-center items-center py-8">
      <div className="animate-pulse text-hormony-coral">Loading your cycle data...</div>
    </div>
  );
  
  return (
    <div className="w-full mx-auto space-y-8">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Left Column - Circular Cycle View */}
        <div className="md:col-span-1 flex flex-col items-center bg-white rounded-xl p-4">
          <CircularCycleView cycleData={cycleData} />
        </div>
        
        {/* Right Column - Hormone Chart & Stats */}
        <div className="md:col-span-2 space-y-6">
          {/* Hormone Chart */}
          <Card className="bg-white border-hormony-beige/30 shadow-sm overflow-hidden">
            <CardContent className="p-4">
              <h3 className="text-lg font-medium mb-3 text-hormony-charcoal">Hormone Levels</h3>
              <HormoneTestPlaceholder cycleData={cycleData} />
            </CardContent>
          </Card>
          
          {/* Cycle Stats Cards */}
          <div className="bg-white rounded-xl p-4 border border-hormony-beige/30 shadow-sm">
            <h3 className="text-lg font-medium mb-3 text-hormony-charcoal">Cycle Timeline</h3>
            <CycleStatCards cycleData={cycleData} />
          </div>
        </div>
      </div>
    </div>
  );
};

export default PeriodTrackingDashboard;
